import { useSessionLocations, useSessionNearbyLocations } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { getLocationSelectionZipCode } from './helpers'
import {
  getLocationCoordinates,
  getLocationDependentFieldReset,
  getLocationSelectionState,
  getLocationStoreFields,
  getSelectedLocationState
} from './state'
import type { EligibilityFormData } from '../types'

type UseLocationSelectionStepArgs = {
  formData: EligibilityFormData
  isLocationLocked: boolean
  updateFormData: (data: Partial<EligibilityFormData>) => void
}

export function useLocationSelectionStep({
  formData,
  isLocationLocked,
  updateFormData
}: UseLocationSelectionStepArgs) {
  const storedSessionToken = useEnrollmentStore(state => state.sessionToken)
  const storedFormData = useEnrollmentStore(state => state.formData)
  const updateEnrollmentStoreFormData = useEnrollmentStore(state => state.updateFormData)
  const sponsorCompanyId = useEnrollmentStore(
    state => state.resumedEnrollment?.sponsorCompanyId ?? null
  )
  const zipCode = getLocationSelectionZipCode(formData, storedFormData.zipCode)
  const enrollmentSessionToken = storedSessionToken?.trim() ?? ''
  const { latitude, longitude } = getLocationCoordinates(zipCode)

  const nearbyLocationsQuery = useSessionNearbyLocations(
    isLocationLocked ? null : latitude,
    isLocationLocked ? null : longitude,
    enrollmentSessionToken,
    sponsorCompanyId
  )
  const sessionLocationsQuery = useSessionLocations(
    enrollmentSessionToken,
    isLocationLocked,
    sponsorCompanyId
  )
  const {
    data: locations = [],
    isPending,
    isError
  } = isLocationLocked ? sessionLocationsQuery : nearbyLocationsQuery
  const isLoadingLocations = isPending
  const {
    activeLocations,
    bothAvailable,
    driverLicenseStateName,
    effectiveLocation,
    hasNoLicenseStateLocations,
    hasNoLocations,
    isLocationSelectDisabled,
    isMissingDriverLicenseState,
    locationOptions,
    locationPlaceholder,
    selectedLocation,
    selectedTransmission
  } = getLocationSelectionState({
    formData,
    isError,
    isLoadingLocations,
    isLocationLocked,
    isPending,
    locations,
    storedLicenseState: storedFormData.licenseState,
    storedLocationExternalId: storedFormData.locationExternalId
  })

  const handleLocationChange = (value: string) => {
    const locationChanged = value !== formData.location

    updateFormData({
      location: value,
      ...(locationChanged ? getLocationDependentFieldReset() : {})
    })

    updateEnrollmentStoreFormData(
      getLocationStoreFields({
        storedProgramClassOnly: storedFormData.programClassOnly,
        storedProgramExternalId: storedFormData.programExternalId,
        storedProgramYardOnly: storedFormData.programYardOnly,
        value
      })
    )

    const selectedLocationState = getSelectedLocationState(activeLocations, value)

    if (selectedLocationState) {
      updateEnrollmentStoreFormData({
        locationState: selectedLocationState
      })
    }
  }

  return {
    zipCode,
    isPending,
    isError,
    isLoadingLocations,
    isMissingDriverLicenseState,
    hasNoLicenseStateLocations,
    hasNoLocations,
    isLocationSelectDisabled,
    driverLicenseStateName,
    locationOptions,
    effectiveLocation,
    selectedLocation,
    selectedTransmission,
    bothAvailable,
    locationPlaceholder,
    handleLocationChange
  }
}
